import { useState } from 'react'
import { ShieldCheck, Plus, Trash } from '@phosphor-icons/react'

// Editor inline de la puntuación de "Auditoría final del certificador" sobre
// un criterio BREEAM. Vive dentro de la ficha del criterio, entre el banner
// verde/rojo (análisis técnico) y la justificación.
//
// Estados visuales:
//   1) Hay auditoría (ev.puntos_auditoria != null) → banner ámbar + botón
//      Editar arriba a la derecha (si canEdit).
//   2) No hay auditoría + canEdit → botón discreto "Registrar auditoría
//      del certificador" con borde punteado.
//   3) editing === true → formulario inline con puntos/fecha/motivo/subcriterios
//      + botones.
//
// Props:
//   - ev        : fila de evaluations del criterio actual (con puntos_auditoria,
//                 motivo_auditoria, fecha_auditoria, subcriteria_rechazados,
//                 points_obtained, points_max)
//   - critData  : def del criterio (para acceder a critData.points, el max)
//   - canEdit   : boolean, si el usuario puede editar
//   - onSave    : async (puntos, motivo, fecha, subcriteriosRechazados) => void
//                 · subcriteriosRechazados es un array [{celda, valor, comentario}]
//                   o null si no hay ninguno
//   - onClear   : async () => void
export default function AuditoriaEditor({ ev, critData, canEdit, onSave, onClear }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ puntos: 0, motivo: '', fecha: '', subcriterios: [] })
  const [saving, setSaving] = useState(false)

  const hasAudit = ev?.puntos_auditoria != null
  const maxPts = critData?.points ?? ev?.points_max ?? 0

  const startEdit = () => {
    setForm({
      puntos: ev?.puntos_auditoria ?? ev?.points_obtained ?? 0,
      motivo: ev?.motivo_auditoria || '',
      fecha: ev?.fecha_auditoria || new Date().toISOString().slice(0, 10),
      subcriterios: Array.isArray(ev?.subcriteria_rechazados)
        ? ev.subcriteria_rechazados.map(s => ({
            celda: s.celda || '',
            valor: s.valor != null ? String(s.valor) : '',
            comentario: s.comentario || '',
          }))
        : [],
    })
    setEditing(true)
  }

  const addSubcriterio = () => {
    setForm(f => ({ ...f, subcriterios: [...f.subcriterios, { celda: '', valor: 'No', comentario: '' }] }))
  }

  const updateSubcriterio = (idx, key, value) => {
    setForm(f => ({
      ...f,
      subcriterios: f.subcriterios.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    }))
  }

  const removeSubcriterio = (idx) => {
    setForm(f => ({ ...f, subcriterios: f.subcriterios.filter((_, i) => i !== idx) }))
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    try {
      const pts = Math.max(0, Math.min(maxPts, parseInt(form.puntos, 10) || 0))
      // Limpiar subcriterios: eliminar filas sin celda (incompletas), parsear valor a
      // número si es numérico, texto en caso contrario.
      const subs = form.subcriterios
        .map(s => ({ celda: (s.celda || '').trim(), valor: (s.valor ?? '').toString().trim(), comentario: (s.comentario || '').trim() }))
        .filter(s => s.celda)
        .map(s => {
          const asNum = Number(s.valor)
          const valorFinal = s.valor !== '' && !Number.isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(s.valor) ? asNum : s.valor
          return { celda: s.celda, valor: valorFinal, comentario: s.comentario || undefined }
        })
      await onSave(pts, form.motivo.trim(), form.fecha || null, subs.length > 0 ? subs : null)
      setEditing(false)
    } catch (e) {
      alert('Error guardando auditoría: ' + (e?.message || e))
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!window.confirm('¿Quitar la puntuación de auditoría? Volverá a usarse la puntuación técnica del asesor.')) return
    setSaving(true)
    try {
      await onClear()
      setEditing(false)
    } catch (e) {
      alert('Error quitando auditoría: ' + (e?.message || e))
    } finally {
      setSaving(false)
    }
  }

  // ── Modo EDIT: formulario inline
  if (editing) {
    return (
      <div style={{ background: '#FFF8E6', padding: '1rem 1.25rem', borderBottom: '1px solid #C98A2E44' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <ShieldCheck size={20} style={{ color: '#C98A2E' }} weight="fill" />
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#7A4A10' }}>
            {hasAudit ? 'Editar auditoría del certificador' : 'Registrar auditoría del certificador'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', color: '#5C4A3A', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
              Puntos otorgados
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input
                type="number" min="0" max={maxPts}
                value={form.puntos}
                onChange={e => setForm({ ...form, puntos: e.target.value })}
                style={{ width: '70px', padding: '0.4rem 0.55rem', border: '1px solid #C98A2E77', borderRadius: '4px', fontSize: '0.9rem', fontFamily: "'IBM Plex Mono', monospace", background: 'white' }}
              />
              <span style={{ fontSize: '0.8rem', color: '#7A6A5A', fontFamily: "'IBM Plex Mono', monospace" }}>/ {maxPts}</span>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', color: '#5C4A3A', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
              Fecha de auditoría
            </label>
            <input
              type="date"
              value={form.fecha}
              onChange={e => setForm({ ...form, fecha: e.target.value })}
              style={{ padding: '0.4rem 0.55rem', border: '1px solid #C98A2E77', borderRadius: '4px', fontSize: '0.85rem', background: 'white' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', fontSize: '0.7rem', color: '#5C4A3A', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
            Motivo (por qué el certificador ajustó la puntuación)
          </label>
          <textarea
            value={form.motivo}
            onChange={e => setForm({ ...form, motivo: e.target.value })}
            rows="3"
            placeholder="Ej: Falta de evidencia fotográfica del interior de las casetas de obra. Aplicando criterio conservador se renuncia al Criterio 2."
            style={{ width: '100%', padding: '0.5rem 0.65rem', border: '1px solid #C98A2E77', borderRadius: '4px', fontSize: '0.8rem', lineHeight: '1.55', fontFamily: 'inherit', resize: 'vertical', background: 'white', color: '#2C2017' }}
          />
        </div>

        {/* Sección subcriterios rechazados */}
        <div style={{ marginBottom: '0.75rem', borderTop: '1px dashed #C98A2E55', paddingTop: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <label style={{ fontSize: '0.7rem', color: '#5C4A3A', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
              Subcriterios rechazados por el certificador (opcional)
            </label>
            <button
              type="button"
              onClick={addSubcriterio}
              style={{ background: 'transparent', color: '#7A4A10', border: '1px dashed #C98A2E77', borderRadius: '3px', padding: '0.25rem 0.6rem', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <Plus size={12} weight="bold" /> Añadir celda
            </button>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#7A6A5A', fontStyle: 'italic', marginBottom: '0.5rem', lineHeight: '1.45' }}>
            Indica las celdas concretas del Excel BREEAM (ej. <code style={{ background: '#FFF', padding: '0 0.25rem', borderRadius: '2px' }}>J83</code>) que el certificador cambió, con el nuevo valor. Esto se aplicará al generar la Herramienta oficial.
          </div>
          {form.subcriterios.length === 0 ? (
            <div style={{ fontSize: '0.72rem', color: '#B0A89E', fontStyle: 'italic', padding: '0.5rem 0' }}>
              (Sin subcriterios rechazados. Suficiente si solo se ajusta la puntuación total.)
            </div>
          ) : (
            form.subcriterios.map((s, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem', padding: '0.5rem', background: 'rgba(255,255,255,0.6)', borderRadius: '4px', border: '1px solid #C98A2E33' }}>
                <input
                  type="text"
                  placeholder="J83"
                  value={s.celda}
                  onChange={e => updateSubcriterio(idx, 'celda', e.target.value.toUpperCase())}
                  style={{ width: '70px', padding: '0.35rem 0.5rem', border: '1px solid #C98A2E77', borderRadius: '3px', fontSize: '0.8rem', fontFamily: "'IBM Plex Mono', monospace", background: 'white', flexShrink: 0 }}
                />
                <input
                  type="text"
                  placeholder="No"
                  value={s.valor}
                  onChange={e => updateSubcriterio(idx, 'valor', e.target.value)}
                  style={{ width: '90px', padding: '0.35rem 0.5rem', border: '1px solid #C98A2E77', borderRadius: '3px', fontSize: '0.8rem', fontFamily: "'IBM Plex Mono', monospace", background: 'white', flexShrink: 0 }}
                />
                <textarea
                  placeholder="Comentario del rechazo (ej: no se aportan planos de instalaciones)"
                  value={s.comentario}
                  onChange={e => updateSubcriterio(idx, 'comentario', e.target.value)}
                  rows="1"
                  style={{ flex: 1, padding: '0.35rem 0.5rem', border: '1px solid #C98A2E77', borderRadius: '3px', fontSize: '0.75rem', lineHeight: '1.4', fontFamily: 'inherit', resize: 'vertical', background: 'white', color: '#2C2017' }}
                />
                <button
                  type="button"
                  onClick={() => removeSubcriterio(idx)}
                  style={{ background: 'transparent', color: '#D4614A', border: '1px solid #D4614A55', borderRadius: '3px', padding: '0.3rem 0.4rem', cursor: 'pointer', flexShrink: 0 }}
                  title="Eliminar"
                >
                  <Trash size={12} />
                </button>
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
          {hasAudit && (
            <button
              onClick={handleClear}
              disabled={saving}
              style={{ background: 'transparent', color: '#8B2A1A', border: '1px solid #D4614A', borderRadius: '4px', padding: '0.4rem 0.85rem', fontSize: '0.75rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', marginRight: 'auto', opacity: saving ? 0.6 : 1 }}
            >
              Quitar auditoría
            </button>
          )}
          <button
            onClick={() => setEditing(false)}
            disabled={saving}
            style={{ background: 'transparent', color: '#5C4A3A', border: '1px solid #B0A89E', borderRadius: '4px', padding: '0.4rem 0.85rem', fontSize: '0.75rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: '#C98A2E', color: 'white', border: 'none', borderRadius: '4px', padding: '0.4rem 1rem', fontSize: '0.75rem', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Guardando…' : 'Guardar auditoría'}
          </button>
        </div>
      </div>
    )
  }

  // ── Modo VIEW: banner ámbar con datos + botón Editar
  if (hasAudit) {
    const rechazos = Array.isArray(ev.subcriteria_rechazados) ? ev.subcriteria_rechazados : []
    return (
      <div style={{ background: '#FFF8E6', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem', borderBottom: '1px solid #C98A2E44' }}>
        <ShieldCheck size={22} style={{ color: '#C98A2E', flexShrink: 0, marginTop: '0.1rem' }} weight="fill" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.825rem', fontWeight: 700, color: '#7A4A10' }}>
              Auditoría final del certificador: <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{ev.puntos_auditoria}/{maxPts} puntos</span>
              {ev.fecha_auditoria && (
                <span style={{ fontWeight: 500, color: '#B0A89E', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                  · {new Date(ev.fecha_auditoria).toLocaleDateString('es-ES')}
                </span>
              )}
            </div>
            {canEdit && (
              <button
                onClick={startEdit}
                style={{ background: 'transparent', color: '#7A4A10', border: '1px solid #C98A2E77', borderRadius: '3px', padding: '0.2rem 0.6rem', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
              >
                Editar
              </button>
            )}
          </div>
          {ev.motivo_auditoria && (
            <div style={{ fontSize: '0.775rem', color: '#5C4A3A', lineHeight: '1.55', marginTop: '0.3rem' }}>
              <strong style={{ color: '#7A4A10' }}>Motivo:</strong> {ev.motivo_auditoria}
            </div>
          )}
          {rechazos.length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.735rem', color: '#5C4A3A' }}>
              <div style={{ fontWeight: 600, color: '#7A4A10', marginBottom: '0.25rem' }}>Subcriterios rechazados:</div>
              {rechazos.map((r, i) => (
                <div key={i} style={{ marginBottom: '0.25rem', lineHeight: '1.5' }}>
                  <code style={{ background: 'rgba(255,255,255,0.6)', padding: '0.05rem 0.3rem', borderRadius: '2px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem' }}>{r.celda}</code>
                  <span style={{ marginLeft: '0.4rem' }}>→ <strong>{String(r.valor)}</strong></span>
                  {r.comentario && <span style={{ marginLeft: '0.4rem', color: '#7A6A5A', fontStyle: 'italic' }}>· {r.comentario}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── No hay auditoría: botón "Registrar" (solo si canEdit)
  if (canEdit) {
    return (
      <div style={{ padding: '0.5rem 1.25rem', background: '#FAF7F3', borderBottom: '1px solid #EDE7DE', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={startEdit}
          style={{ background: 'transparent', color: '#7A4A10', border: '1px dashed #C98A2E77', borderRadius: '3px', padding: '0.3rem 0.75rem', fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
        >
          <ShieldCheck size={14} weight="fill" style={{ color: '#C98A2E' }} />
          Registrar auditoría del certificador
        </button>
      </div>
    )
  }

  return null
}
